<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ColumnGroup from "primevue/columngroup";
import Row from "primevue/row";
import { ProfileContractorApi } from "../api";
import type { GeneratedProfileCount } from "../types/StatisticsTypes.ts";
import { exportFileName, presetDays, StatisticsPreset, sumCounts, toPeriod } from "../utils/generationStatistics.ts";

/**
 * How many profiles of each product were generated in a period, real and test apart (RDT-435). The
 * numbers come from profile-contractor's `pdfs` table; see profile-generator/docs/generation-statistics.
 */
const { t } = useI18n();

const presets: StatisticsPreset[] = ["thisMonth", "lastMonth", "thisYear"];
const preset = ref<StatisticsPreset | null>("thisMonth");
const days = ref<(Date | null)[] | null>(presetDays("thisMonth"));
const rows = ref<GeneratedProfileCount[]>([]);
const selectedProducts = ref<string[]>([]);
const loading = ref(false);
const failed = ref(false);
const table = ref();

const presetOptions = computed(() =>
    presets.map((value) => ({ value, label: t(`generationStatistics.presets.${value}`) })),
);
const productOptions = computed(() => rows.value.map((row) => row.product).sort());
const shownRows = computed(() =>
    selectedProducts.value.length === 0
        ? rows.value
        : rows.value.filter((row) => selectedProducts.value.includes(row.product)),
);
const sum = computed(() => sumCounts(shownRows.value));
const pickedDays = computed<[Date, Date] | null>(() => {
    const [first, last] = days.value ?? [];
    return first && last ? [first, last] : null;
});
const fileName = computed(() => (pickedDays.value ? exportFileName(...pickedDays.value) : "profile-statistics"));

function choosePreset(value: StatisticsPreset | null) {
    if (value) {
        days.value = presetDays(value);
    }
}

async function load() {
    if (!pickedDays.value) {
        return;
    }
    const { from, to } = toPeriod(...pickedDays.value);
    loading.value = true;
    failed.value = false;
    try {
        rows.value = (await ProfileContractorApi.fetchGeneratedProfiles(from, to)).products;
        // Keep only selected products that still occur, so the filter never hides everything silently.
        selectedProducts.value = selectedProducts.value.filter((product) => productOptions.value.includes(product));
    } catch (error) {
        console.error("Could not load the generation statistics", error);
        rows.value = [];
        failed.value = true;
    } finally {
        loading.value = false;
    }
}

function exportCsv() {
    table.value?.exportCSV();
}

watch(days, load);
onMounted(load);
</script>

<template>
    <h1 class="page-title mb-1">{{ t("generationStatistics.title") }}</h1>
    <p class="text-content-muted mt-0">{{ t("generationStatistics.intro") }}</p>

    <div class="mt-6 flex flex-wrap items-end gap-4">
        <SelectButton
            id="generation-statistics_presets"
            data-cy="generation-statistics-presets"
            v-model="preset"
            :options="presetOptions"
            optionLabel="label"
            optionValue="value"
            @update:modelValue="choosePreset"
        />
        <div class="w-64">
            <label for="generation-statistics_period" class="mb-1 block text-sm">{{ t("generationStatistics.period") }}</label>
            <DatePicker
                inputId="generation-statistics_period"
                data-cy="generation-statistics-period"
                v-model="days"
                selectionMode="range"
                dateFormat="dd.mm.yy"
                :manualInput="false"
                showIcon
                fluid
                @date-select="preset = null"
            />
        </div>
        <div>
            <label for="generation-statistics_products" class="mb-1 block text-sm">{{ t("generationStatistics.products") }}</label>
            <MultiSelect
                inputId="generation-statistics_products"
                data-cy="generation-statistics-products"
                v-model="selectedProducts"
                :options="productOptions"
                :placeholder="t('generationStatistics.allProducts')"
                display="chip"
                filter
                class="min-w-72"
            />
        </div>
    </div>

    <Message v-if="failed" id="generation-statistics_error" data-cy="generation-statistics-error" severity="error" class="mt-6">
        {{ t("generationStatistics.error") }}
    </Message>

    <DataTable
        id="generation-statistics_table"
        data-cy="generation-statistics-table"
        ref="table"
        class="mt-6"
        :value="shownRows"
        :loading="loading"
        sortField="total"
        :sortOrder="-1"
        csvSeparator=";"
        :exportFilename="fileName"
        tableStyle="min-width: 40rem"
    >
        <template #header>
            <div class="flex items-center justify-between gap-4">
                <span class="text-sm">{{ t("generationStatistics.testHint") }}</span>
                <Button
                    id="generation-statistics_export"
                    data-cy="generation-statistics-export"
                    icon="pi pi-download"
                    text
                    size="small"
                    :label="t('buttons.export')"
                    :disabled="shownRows.length === 0"
                    @click="exportCsv"
                />
            </div>
        </template>
        <template #empty>{{ t("generationStatistics.empty") }}</template>
        <Column field="product" :header="t('generationStatistics.columns.product')" sortable />
        <Column field="real" :header="t('generationStatistics.columns.real')" sortable />
        <Column field="test" :header="t('generationStatistics.columns.test')" sortable />
        <Column field="total" :header="t('generationStatistics.columns.total')" sortable />
        <ColumnGroup type="footer">
            <Row>
                <Column :footer="t('generationStatistics.sum')" />
                <Column :footer="String(sum.real)" />
                <Column :footer="String(sum.test)" />
                <Column :footer="String(sum.total)" />
            </Row>
        </ColumnGroup>
    </DataTable>

    <Message id="generation-statistics_limits" data-cy="generation-statistics-limits" severity="secondary" class="mt-6">
        <strong>{{ t("generationStatistics.limits.title") }}</strong>
        <ul class="mt-1 list-disc pl-5">
            <li>{{ t("generationStatistics.limits.noTimestamps") }}</li>
            <li>{{ t("generationStatistics.limits.deletedMissing") }}</li>
            <li>{{ t("generationStatistics.limits.testDetection") }}</li>
        </ul>
    </Message>
</template>
